/*
 * WELCOME SCREEN (2026-10-17, Alex) - country, language and cookies in one screen. It replaces the Orbe pop-up and
 * Shopify's cookie banner. Markup: sections/cf-welcome.liquid (fetched in the language the screen opens in);
 * loader and settings: snippets/cf-welcome.liquid; looks: theme.css, WELCOME SCREEN.
 *
 * Prices differ by country, so the first job is that every visitor shops in their own country's store.
 *
 * FIRST VISIT. The screen opens when the visitor needs at least one of:
 * - the country: Shopify's geolocation (Customer Privacy API getRegion(), else /browsing_context_suggestions.json)
 *   is a country we ship to and not the shop's current one. The visitor's country is preselected.
 * - a note: the visitor's country has no shop of its own (not in a market). We still ship there, but the order goes by
 *   email: "There's no online shop for Brazil yet, but we can still ship there. Contact us ..." (2026-10-17, Alex).
 * - the language: the browser reads one of the languages the target country's shop has before any other, and the page
 *   is not in it. The screen then opens in that language, with a two-way switch (that one | the page's).
 * - the cookies: Shopify's consent API says this visitor's region asks for consent and none is given yet.
 * A country we do not sell to for now (snippets/cf-blocked-countries) gets the product pages' sentence when chosen.
 * The first-visit screen has no close button and Esc does not close it.
 *
 * REMEMBERED (localStorage cf-welcome: { country, language }). What the visitor answered, and only that: the country
 * whenever they confirmed one (the field, or the "Shipping to" line they went on with), the language only when they
 * were asked it. What is remembered is not asked again; cookies are asked until answered, as Shopify's banner did.
 *
 * COMING BACK. Ads link the plain cityframes.de, in English. A visitor whose remembered country or language differs
 * and who lands here from outside the shop is taken back to it (once a visit, never while browsing the shop).
 *
 * LATER. [data-localization-open] (the header's flag, Good to know's "Change") opens country and language only, the
 * language as the same switch with every language the chosen country's shop has (by name where the names fit on one
 * line, else by code: EN DE ES FR IT), left out when the shop has one; [data-cookie-settings-open] (the footer) opens the cookie
 * settings only. Both close with the cross or Esc.
 *
 * Cookies go to Shopify's Customer Privacy API (setTrackingConsent), which tells everything else on the page,
 * assets/cf-tracking.js included (visitorConsentCollected). Country and language go through Shopify's localization
 * form, both in one step, back to the same page; the language sent is always one the country's shop has.
 *
 * OTHER DOMAINS. Four markets have their own: the UK (cityframes.uk), Switzerland (.ch), Italy (.it) and France (.fr).
 * Consent and this browser's storage belong to one domain, so a UK visitor who answers on cityframes.de would be asked
 * again on cityframes.uk. The answer therefore travels in the address Shopify sends them back to (return_to keeps its
 * query across domains): ?cf_welcome=<analytics><marketing><preferences> as 1/0, or "-" when there is no cookie choice,
 * then "l" when the language was chosen. The page it arrives on takes it once - the cookie choice only where none is
 * given yet - and removes it from the address.
 *
 * NO SHOP OF ITS OWN. A country in no market is still one we ship to, by email order. The list ends with "My country
 * isn't listed", and the search also finds those countries by name ("Brazil · No online shop yet"). Choosing either keeps
 * the visitor in the current shop and shows the sentence that says so, with the contact link (state.other: '' for the
 * row, else the country's code).
 *
 * SEARCH. The list finds a country by its name in any of the shop's languages and the browser's own (the browser
 * names them: Intl.DisplayNames), so "Schweiz", "Suisse" and "Switzerland" all find it, as does its code, "CH"
 * (first), and what people type that no name contains (ALSO_CALLED: USA, UK, England, UAE, Holland ...).
 */
(() => {
  if (customElements.get('cf-welcome')) return;

  const STORE = 'cf-welcome';
  const CARRY = 'cf_welcome';
  const SEARCH_LANGUAGES = ['en', 'de', 'fr', 'it', 'es'];
  // What people type that no official name contains.
  const ALSO_CALLED = {
    US: 'USA America Amerika',
    GB: 'UK England Scotland Wales Great Britain Großbritannien Grande-Bretagne Gran Bretagna Gran Bretaña Inghilterra Inglaterra Schottland',
    AE: 'UAE VAE EAU Emirates Emirate Emirati Dubai Abu Dhabi',
    NL: 'Holland Holanda Olanda Hollande',
    CZ: 'Czech Republic Tschechische Republik Tschechei',
    KR: 'Korea Korean Corée Corea',
    HK: 'Hongkong',
    BA: 'Bosnia',
    MK: 'Macedonia Mazedonien Macédoine'
  };

  const read = () => {
    try {
      return JSON.parse(localStorage.getItem(STORE)) || {};
    } catch (error) {
      return {};
    }
  };
  // Adds to what is remembered; never forgets an earlier answer on its own.
  const remember = (answer) => {
    try {
      localStorage.setItem(STORE, JSON.stringify(Object.assign(read(), answer, { at: Date.now() })));
    } catch (error) {
      // private mode: the screen will simply ask again
    }
  };
  const fold = (text) => String(text || '').toLocaleLowerCase().normalize('NFD').replace(/\p{M}/gu, '');
  const regionName = (code, language) => {
    try {
      return new Intl.DisplayNames([language], { type: 'region' }).of(code) || code;
    } catch (error) {
      return code;
    }
  };

  // SETTLED: the first visit's screen has been and gone, or will not come (2026-10-28) - window.cfWelcomeSettled and a
  // cf:welcome-settled event, so another first-visit window (the corporate gifts' settings) never opens on top of it.
  const settle = () => {
    if (window.cfWelcomeSettled) return;
    window.cfWelcomeSettled = true;
    document.dispatchEvent(new Event('cf:welcome-settled'));
  };

  // Shopify's Customer Privacy API, the one assets/cf-tracking.js reads; null when it cannot load.
  let privacyPromise = null;
  const privacyApi = () => {
    if (!privacyPromise) {
      privacyPromise = new Promise((resolve) => {
        const api = () => (window.Shopify && window.Shopify.customerPrivacy) || null;
        if (api()) return resolve(api());
        if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return resolve(null);
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => resolve(error ? null : api()));
      });
    }
    return privacyPromise;
  };

  class CfWelcome extends HTMLElement {
    connectedCallback() {
      const data = this.dataset;
      this.country = data.country;
      this.language = data.language;
      this.countries = data.countries.split(',');
      this.languages = data.languages.split(',');
      this.popular = data.popular.split(',');
      this.regions = data.consentRegions.split(',');
      this.blocked = (data.blocked || '').split(',').filter(Boolean);
      this.roots = Object.fromEntries(data.roots.split(',').map((pair) => pair.split('=')));
      this.countryLanguages = new Map((data.countryLanguages || '').split(';').filter(Boolean).map((entry) => {
        const [code, list] = entry.split(':');
        return [code, (list || '').split(',').filter(Boolean)];
      }));
      this.dialog = this.querySelector('dialog');
      this.cache = {};
      this.state = null;
      this.active = -1;

      this.dialog.addEventListener('cancel', (event) => {
        if (!this.state || this.state.mode === 'first') event.preventDefault();
      });
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('cf-welcome-open');
        this.state = null;
        this.pinned = null;
        settle();
      });
      this.dialog.addEventListener('click', (event) => this.onClick(event));
      this.dialog.addEventListener('pointerdown', (event) => {
        if (this.listOpen() && !event.target.closest('.cf-welcome__picker')) this.closeList(false);
      });
      this.dialog.addEventListener('input', (event) => {
        if (event.target.matches('[data-country-search]')) this.search(event.target.value);
      });
      this.dialog.addEventListener('keydown', (event) => {
        if (event.target.matches('[data-country-search]')) this.searchKey(event);
      });
      window.addEventListener('resize', () => this.fitLanguages(), { passive: true });

      const opener = (event) => event.target.closest && event.target.closest('[data-localization-open], [data-cookie-settings-open]');
      document.addEventListener('click', (event) => {
        const button = opener(event);
        if (!button) return;
        event.preventDefault();
        this.open({ mode: button.hasAttribute('data-localization-open') ? 'change' : 'cookies' });
      });
      // a hover or a touch on one of them fetches the screen before the click
      const warm = (event) => {
        if (opener(event)) this.load(this.language).catch(() => {});
      };
      document.addEventListener('pointerover', warm, { passive: true });
      document.addEventListener('touchstart', warm, { passive: true });

      this.takeCarried();
      if (!(window.Shopify && window.Shopify.designMode)) this.firstVisit().catch(settle);
      else settle();
    }

    // The languages of a country's shop (its market's).
    languagesOf(code) {
      return this.countryLanguages.get(code) || this.languages;
    }

    // The language to send for a country: the one wanted when its shop has it, else the page's, else English.
    languageFor(code, wanted) {
      const list = this.languagesOf(code);
      return [wanted, this.language, 'en'].find((language) => language && list.includes(language)) || list[0] || this.language;
    }

    // The answer given on another of the shop's domains (see OTHER DOMAINS above).
    takeCarried() {
      const params = new URLSearchParams(window.location.search);
      const carried = params.get(CARRY);
      if (carried === null) return;
      const match = /^([01]{3}|-)(l?)$/.exec(carried);
      if (match) {
        remember(match[2] ? { country: this.country, language: this.language } : { country: this.country });
        if (match[1] !== '-') {
          this.carried = { analytics: match[1][0] === '1', marketing: match[1][1] === '1', preferences: match[1][2] === '1' };
        }
      }
      params.delete(CARRY);
      const query = params.toString();
      window.history.replaceState(window.history.state, '', window.location.pathname + (query ? `?${query}` : '') + window.location.hash);
    }

    // --- when to open ---------------------------------------------------------------------------------------------

    async firstVisit() {
      const answered = read();
      // a first-timer will most likely see the screen: fetch it while the visitor is located
      if (!answered.country) this.load(this.browserLanguage(this.languages) || this.language).catch(() => {});

      const privacy = await privacyApi();
      if (this.returnTo(answered)) return;
      const detected = await this.detect(privacy);
      const served = this.countries.includes(detected);

      let cookies = false;
      if (privacy) {
        try {
          const before = privacy.currentVisitorConsent() || {};
          if (this.carried && !before.marketing && !before.analytics && !before.preferences) {
            await new Promise((resolve) => {
              setTimeout(resolve, 3000);
              privacy.setTrackingConsent(this.carried, resolve);
            });
          }
          const given = privacy.currentVisitorConsent() || {};
          const unanswered = !given.marketing && !given.analytics && !given.preferences;
          cookies = unanswered && (privacy.shouldShowBanner() || this.regions.includes(detected));
        } catch (error) {
          cookies = false;
        }
      }

      const askCountry = !answered.country && served && detected !== this.country;
      const unserved = !answered.country && Boolean(detected) && !served ? detected : null;
      const target = askCountry ? detected : this.country;
      let suggested = answered.language ? null : this.browserLanguage(this.languagesOf(target));
      if (suggested === this.language) suggested = null;
      if (!askCountry && !unserved && !suggested && !cookies) return settle();

      this.open({
        mode: 'first',
        detected: served ? detected : null,
        country: target,
        askCountry,
        unserved,
        suggested,
        language: suggested || this.language,
        cookies
      });
    }

    async detect(privacy) {
      if (this.detected !== undefined) return this.detected;
      let region = '';
      try {
        region = (privacy && privacy.getRegion()) || '';
      } catch (error) {
        region = '';
      }
      let country = region.slice(0, 2).toUpperCase();
      if (!country) {
        try {
          const response = await fetch('/browsing_context_suggestions.json?country%5Benabled%5D=true', { headers: { Accept: 'application/json' } });
          const data = await response.json();
          country = (((data || {}).detected_values || {}).country || {}).handle || '';
        } catch (error) {
          country = '';
        }
      }
      this.detected = country;
      return country;
    }

    // Back to the country and language answered before, for a visitor arriving from outside the shop.
    returnTo(answered) {
      const country = answered.country && answered.country !== this.country && this.countries.includes(answered.country) ? answered.country : this.country;
      const language = answered.language && this.languagesOf(country).includes(answered.language) ? answered.language : null;
      if (country === this.country && (!language || language === this.language)) return false;
      let fromShop = false;
      try {
        fromShop = Boolean(document.referrer) && /(^|\.)cityframes\.[a-z]+$/.test(new URL(document.referrer).hostname);
      } catch (error) {
        fromShop = false;
      }
      if (fromShop) return false;
      try {
        if (sessionStorage.getItem('cf-welcome-moved')) return false;
        sessionStorage.setItem('cf-welcome-moved', '1');
      } catch (error) {
        return false;
      }
      this.switchTo(country, this.languageFor(country, language), this.carry(null, Boolean(language)));
      return true;
    }

    // The first of the browser's languages that is in the list.
    browserLanguage(list) {
      for (const tag of navigator.languages || [navigator.language || '']) {
        const code = String(tag).slice(0, 2).toLowerCase();
        if (list.includes(code)) return code;
      }
      return null;
    }

    // --- the screen --------------------------------------------------------------------------------------------------

    load(language) {
      if (!this.cache[language]) {
        const root = this.roots[language] || '/';
        this.cache[language] = fetch(`${root}?section_id=cf-welcome`, { credentials: 'same-origin' })
          .then((response) => {
            if (!response.ok) throw new Error(`cf-welcome: ${response.status}`);
            return response.text();
          })
          .then((html) => {
            const content = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cf-welcome-content]');
            if (!content) throw new Error('cf-welcome: no content');
            return content;
          })
          .catch((error) => {
            delete this.cache[language];
            throw error;
          });
      }
      return this.cache[language];
    }

    async open(options) {
      const state = Object.assign({
        mode: 'first',
        detected: null,
        country: this.country,
        askCountry: false,
        unserved: null,
        other: null,
        changing: false,
        suggested: null,
        language: this.language,
        cookies: false
      }, options);
      state.view = state.view || (state.mode === 'cookies' ? 'settings' : 'main');
      state.target = state.target || state.language;
      if (state.mode === 'change' && !state.detected) {
        const detected = await this.detect(await privacyApi());
        state.detected = this.countries.includes(detected) ? detected : null;
      }
      this.state = state;

      let content;
      try {
        content = await this.load(state.language);
      } catch (error) {
        if (state.language === this.language) return;
        state.language = this.language;
        try {
          content = await this.load(state.language);
        } catch (error2) {
          return;
        }
      }
      if (this.state !== state) return;

      // switching language in the open screen: the cookie text keeps at least the room it had, so a shorter language
      // does not pull the buttons up (Alex, 2026-10-18)
      const before = this.dialog.open && this.content ? this.content.querySelector('[data-part="cookies"] p') : null;
      const room = before ? before.getBoundingClientRect().height : 0;
      this.content = content.cloneNode(true);
      this.dialog.replaceChildren(this.content);
      if (room) this.content.querySelector('[data-part="cookies"] p').style.minHeight = `${room}px`;
      this.indexRows();
      this.render();

      if (!this.dialog.open) {
        document.documentElement.classList.add('cf-welcome-open');
        this.dialog.showModal();
      }
      this.pin();
      this.fitLanguages();
      // change: straight to the country; otherwise the screen itself, so no field shows a focus ring before it is used
      const focus = state.mode === 'change' ? this.content.querySelector('[data-action="list"]') : this.dialog;
      focus.focus();
    }

    // Each country's names to search: Shopify's, and the browser's in the shop's languages and the visitor's own.
    indexRows() {
      const screen = this.content.dataset.language;
      if (!this.namers) {
        const own = (navigator.languages || []).map((tag) => String(tag).slice(0, 2).toLowerCase());
        this.namers = [...new Set([...SEARCH_LANGUAGES, ...this.languages, ...own])].map((language) => {
          try {
            return new Intl.DisplayNames([language], { type: 'region' });
          } catch (error) {
            return null;
          }
        }).filter(Boolean);
      }
      this.rows = [...this.content.querySelectorAll('.cf-welcome__option')];
      this.rows.forEach((row) => {
        const names = [row.querySelector('.cf-welcome__option-name').textContent, ...(ALSO_CALLED[row.dataset.country] || '').split(' ')];
        this.namers.forEach((namer) => {
          try {
            names.push(namer.of(row.dataset.country));
          } catch (error) {
            // a code the browser does not know: Shopify's name is enough
          }
        });
        row.names = [...new Set(names.map(fold))];
      });
      this.collator = new Intl.Collator(screen);
    }

    // The screen opens centred, then its top stays where it is while it is open: another language's longer text, the
    // country field or the cookie settings grow it downwards instead of moving all of it (Alex, 2026-10-18).
    pin() {
      if (this.pinned === null || this.pinned === undefined) {
        const padding = parseFloat(getComputedStyle(this.dialog).paddingTop) || 0;
        this.pinned = Math.max(0, this.content.offsetTop - padding);
      }
      this.content.style.marginBlock = `${this.pinned}px auto`;
    }

    option(code) {
      return this.rows.find((row) => row.dataset.country === code) || null;
    }

    // The languages by name where they fit on one line, else by code: five names need about 434px and a phone has
    // 288-398, the codes 275 (2026-10-19, Alex). The name stays each button's accessible name.
    fitLanguages() {
      if (!this.dialog.open || !this.content) return;
      const field = this.content.querySelector('[data-part="language-switch"]');
      if (!field || field.hidden) return;
      const buttons = [...field.querySelectorAll('[data-language-option]')];
      buttons.forEach((button) => {
        button.dataset.name = button.dataset.name || button.textContent.trim();
        button.textContent = button.dataset.name;
        button.removeAttribute('aria-label');
      });
      const track = field.querySelector('.cf-welcome__language-track');
      if (Math.max(track.scrollWidth, track.offsetWidth) <= field.clientWidth) return;
      buttons.forEach((button) => {
        button.textContent = button.dataset.languageOption.toUpperCase();
        button.setAttribute('aria-label', button.dataset.name);
      });
    }

    render() {
      const state = this.state;
      const content = this.content;
      const first = state.mode === 'first';
      const change = state.mode === 'change';
      const showCountry = change || (first && (state.askCountry || state.changing));
      const languages = this.languagesOf(state.country);

      // the language: on a first visit the switch offers the browser's language and the page's, where the chosen
      // country's shop has them; in change, every language the country's shop has. One language: no question at all.
      let switchOptions = [];
      if (change) switchOptions = languages;
      else if (first && state.suggested) switchOptions = [state.suggested, this.language].filter((code) => languages.includes(code));
      const showSwitch = switchOptions.length > 1;
      if (change && !languages.includes(state.target)) state.target = this.languageFor(state.country, state.target);
      state.languageAsked = showSwitch;

      const other = state.other !== null && state.other !== undefined;
      const show = {
        close: !first,
        sub: first && showCountry,
        country: showCountry,
        unserved: (first && Boolean(state.unserved) && !showCountry) || (other && state.other !== ''),
        other: other && state.other === '',
        shipline: first && !showCountry && !state.unserved,
        blocked: (first || change) && !other && this.blocked.includes(state.country),
        'language-switch': showSwitch,
        cookies: first && state.cookies,
        continue: first && !state.cookies,
        apply: change,
        back: state.mode !== 'cookies'
      };
      content.querySelectorAll('[data-part]').forEach((element) => {
        element.hidden = !show[element.dataset.part];
      });
      content.querySelector('[data-view="main"]').hidden = state.view !== 'main';
      content.querySelector('[data-view="settings"]').hidden = state.view !== 'settings';
      this.dialog.setAttribute('aria-labelledby', state.view === 'settings' ? 'cf-welcome-settings-title' : 'cf-welcome-title');

      const title = content.querySelector('#cf-welcome-title');
      title.textContent = change ? title.dataset.titleChange : title.dataset.titleWelcome;

      // the country: field, shipping line, notes. A shop that offers no country to choose (a B2B buyer, whose company
      // location fixes it; 2026-10-20) shows its country by name and the shop's currency, and the field does not open.
      const fixed = !this.rows.length;
      const row = this.option(state.country);
      const name = row ? row.querySelector('.cf-welcome__option-name').textContent.trim() : regionName(state.country, content.dataset.language);
      const shopCurrency = window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : '';
      const currency = row ? row.dataset.currency : (fixed && shopCurrency) || '';
      content.querySelector('[data-action="list"]').disabled = fixed;
      const the = (code) => ((content.dataset.the || '').split(',').includes(code) ? 'the ' : '');
      const screen = content.dataset.language;
      content.querySelectorAll('[data-country-flag]').forEach((image) => {
        if (row) image.src = row.dataset.flag;
        image.hidden = !row || Boolean(other && image.closest('[data-action="list"]'));
      });
      content.querySelector('[data-country-earth]').hidden = !other;
      const value = content.querySelector('[data-country-value]');
      if (other) value.textContent = state.other ? regionName(state.other, screen) : value.dataset.another;
      else value.textContent = currency ? `${name} · ${currency}` : name;
      content.querySelector('[data-country-name]').textContent = the(state.country) + name;
      content.querySelector('[data-country-currency]').textContent = currency;
      content.querySelectorAll('[data-blocked-country]').forEach((slot) => {
        slot.textContent = name;
      });
      const noShop = other && state.other ? state.other : state.unserved;
      if (noShop) content.querySelector('[data-unserved-country]').textContent = the(noShop) + regionName(noShop, screen);

      // the language switch: a first visit's two in the order offered; change's in the chosen country's shop's order
      // (the markup has every language of every shop - 2026-10-21), as a field under the country (label above, the
      // switch across the width), its choice made by Apply
      const languageSwitch = content.querySelector('[data-part="language-switch"]');
      languageSwitch.classList.toggle('cf-welcome__languages--field', change);
      languageSwitch.querySelector('#cf-welcome-language-label').classList.toggle('text-subdued', !change);
      const track = languageSwitch.querySelector('.cf-welcome__language-track');
      [...switchOptions].reverse().forEach((code) => {
        const button = track.querySelector(`[data-language-option="${code}"]`);
        if (button) track.prepend(button);
      });
      track.querySelectorAll('[data-language-option]').forEach((button) => {
        const code = button.dataset.languageOption;
        button.hidden = !switchOptions.includes(code);
        button.setAttribute('aria-pressed', String(code === (change ? state.target : state.language)));
      });
      this.fitLanguages();

      // the cookie settings start from what the visitor chose before
      if (state.view === 'settings' && !state.purposesSet) {
        state.purposesSet = true;
        const given = (window.Shopify && window.Shopify.customerPrivacy && window.Shopify.customerPrivacy.currentVisitorConsent()) || {};
        content.querySelectorAll('[data-purpose]:not([disabled])').forEach((input) => {
          input.checked = given[input.dataset.purpose] === 'yes';
        });
      }
    }

    // --- what the visitor does ---------------------------------------------------------------------------------------

    onClick(event) {
      const state = this.state;
      if (!state) return;
      const target = event.target;

      // a click on the empty screen closes it, but never the first-visit screen
      if ((target === this.dialog || target === this.content) && state.mode !== 'first') return this.close();

      const option = target.closest('.cf-welcome__option');
      if (option) return option.dataset.other !== undefined ? this.chooseOther(option.dataset.other) : this.choose(option.dataset.country);

      const language = target.closest('[data-language-option]');
      if (language && state.mode === 'change') {
        state.target = language.dataset.languageOption;
        this.render();
        return;
      }
      if (language) {
        if (language.dataset.languageOption !== state.language) {
          this.open(Object.assign({}, state, { language: language.dataset.languageOption, target: language.dataset.languageOption, purposesSet: false }));
        }
        return;
      }

      const named = target.closest('button[name]');
      if (named) {
        if (named.name === 'cf-accept') return this.consent({ analytics: true, marketing: true, preferences: true });
        if (named.name === 'cf-necessary') return this.consent({ analytics: false, marketing: false, preferences: false });
        if (named.name === 'cf-save') {
          const choice = {};
          this.content.querySelectorAll('[data-purpose]:not([disabled])').forEach((input) => {
            choice[input.dataset.purpose] = input.checked;
          });
          return this.consent(choice);
        }
        if (named.name === 'cf-continue' || named.name === 'cf-apply') return this.finish();
      }

      const action = target.closest('[data-action]');
      if (!action) return;
      switch (action.dataset.action) {
        case 'close':
          return this.close();
        case 'list':
          return this.listOpen() ? this.closeList(true) : this.openList();
        case 'list-close':
          return this.closeList(true);
        case 'change':
          state.changing = true;
          this.render();
          return this.openList();
        case 'settings':
          state.view = 'settings';
          this.render();
          return this.content.querySelector('[data-view="settings"] .switch:not([disabled])').focus();
        case 'back':
          state.view = 'main';
          this.render();
          return this.content.querySelector('[data-action="settings"]').focus();
        default:
      }
    }

    async consent(choice) {
      if (this.state) this.state.choice = choice;
      const privacy = await privacyApi();
      if (privacy) {
        await new Promise((resolve) => {
          setTimeout(resolve, 3000);
          try {
            privacy.setTrackingConsent(choice, resolve);
          } catch (error) {
            resolve();
          }
        });
      }
      if (!this.state) return;
      if (this.state.mode === 'cookies') return this.close();
      this.finish();
    }

    // Remember the answer; switch country and language in one step when either changed, else just close.
    finish() {
      const state = this.state;
      if (state.mode === 'cookies') return this.close();
      const language = this.languageFor(state.country, state.mode === 'change' ? state.target : state.language);
      remember(state.languageAsked ? { country: state.country, language } : { country: state.country });
      if (state.country === this.country && language === this.language) return this.close();
      this.switchTo(state.country, language, this.carry(state.choice, state.languageAsked));
    }

    // Shopify's localization form: country and language in one step, back to this page, the answer carried along.
    switchTo(country, language, carry) {
      const params = new URLSearchParams(window.location.search);
      params.set(CARRY, carry);
      const fields = {
        form_type: 'localization',
        utf8: '✓',
        _method: 'put',
        country_code: country,
        language_code: language,
        return_to: `${window.location.pathname}?${params}`
      };
      const form = document.createElement('form');
      form.method = 'post';
      form.action = '/localization';
      form.hidden = true;
      Object.entries(fields).forEach(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.append(input);
      });
      document.body.append(form);
      form.submit();
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    // What travels to another domain: the cookie choice (the one just made, else the one given before here, else "-"),
    // then "l" when the language was answered.
    carry(choice, languageAsked) {
      if (!choice) {
        try {
          const given = window.Shopify.customerPrivacy.currentVisitorConsent() || {};
          if (given.marketing || given.analytics || given.preferences) {
            choice = { analytics: given.analytics === 'yes', marketing: given.marketing === 'yes', preferences: given.preferences === 'yes' };
          }
        } catch (error) {
          choice = null;
        }
      }
      const cookies = choice ? ['analytics', 'marketing', 'preferences'].map((key) => (choice[key] ? '1' : '0')).join('') : '-';
      return cookies + (languageAsked ? 'l' : '');
    }

    // --- the country list ----------------------------------------------------------------------------------------------

    listOpen() {
      const list = this.content && this.content.querySelector('[data-country-list]');
      return Boolean(list && !list.hidden);
    }

    openList() {
      if (!this.rows.length) return;
      const panel = this.content.querySelector('[data-country-list]');
      const input = panel.querySelector('[data-country-search]');
      input.value = '';
      this.fill('');
      panel.hidden = false;
      this.content.querySelector('[data-action="list"]').setAttribute('aria-expanded', 'true');
      const list = panel.querySelector('[role="listbox"]');
      const selected = list.querySelector('[aria-selected="true"]');
      list.scrollTop = 0;
      if (selected) {
        const top = selected.offsetTop - list.offsetTop;
        if (top > list.clientHeight - 60) list.scrollTop = top - list.clientHeight / 2;
      }
      input.focus({ preventScroll: true });
    }

    closeList(refocus) {
      const panel = this.content.querySelector('[data-country-list]');
      panel.hidden = true;
      this.active = -1;
      const button = this.content.querySelector('[data-action="list"]');
      button.setAttribute('aria-expanded', 'false');
      if (refocus) button.focus();
    }

    choose(code) {
      this.state.country = code;
      this.state.other = null;
      this.closeList(false);
      this.render();
      this.content.querySelector('[data-action="list"]').focus();
    }

    // A country without a shop (its code), or "My country isn't listed" (''): the visitor stays in this shop.
    chooseOther(code) {
      this.state.other = code;
      this.state.country = this.country;
      this.closeList(false);
      this.render();
      this.content.querySelector('[data-action="list"]').focus();
    }

    // A row for a country without a shop, or for "My country isn't listed" ('').
    otherRow(code) {
      const list = this.content.querySelector('[role="listbox"]');
      const item = this.content.querySelector('[data-other-option]').content.firstElementChild.cloneNode(true);
      item.dataset.other = code;
      item.id = `cf-welcome-other-${code || 'any'}`;
      item.querySelector('.cf-welcome__option-name').textContent = code ? regionName(code, this.content.dataset.language) : list.dataset.other;
      item.querySelector('.text-subdued').textContent = code ? list.dataset.noShop : '';
      item.setAttribute('aria-selected', String(this.state.other === code));
      return item;
    }

    // Every country the browser can name that has no shop of ours, with its names to search (made on the first search).
    otherCountries() {
      if (!this.others) {
        // Not countries, and the retired codes the browser still names after the country that replaced them (FX
        // "France", DD "Germany", UK, SU ...): each listed a shop's country a second time, as one without a shop.
        const skip = new Set(
          'AC CP DG EA EU EZ IC QO TA UN XA XB ZZ AN BU CS DD DY FX HV NH RH SU TP UK VD YD YU ZR'.split(' ')
        );
        const shop = new Set(this.rows.map((row) => row.dataset.country));
        let english = null;
        try {
          english = new Intl.DisplayNames(['en'], { type: 'region', fallback: 'none' });
        } catch (error) {
          english = null;
        }
        this.others = [];
        for (let a = 65; english && a <= 90; a += 1) {
          for (let b = 65; b <= 90; b += 1) {
            const code = String.fromCharCode(a, b);
            if (skip.has(code) || shop.has(code) || !english.of(code)) continue;
            const names = this.namers.map((namer) => {
              try {
                return fold(namer.of(code));
              } catch (error) {
                return '';
              }
            });
            this.others.push({ code, names: [...new Set(names.filter(Boolean))] });
          }
        }
      }
      return this.others;
    }

    // Your location, the popular ones, all A-Z, then "My country isn't listed"; while searching, every match, those
    // starting with it first, then the countries without a shop that match.
    fill(query) {
      const state = this.state;
      const list = this.content.querySelector('[role="listbox"]');
      const shopName = (row) => row.querySelector('.cf-welcome__option-name').textContent;
      const byName = (a, b) => this.collator.compare(shopName(a), shopName(b));
      const group = (text) => {
        const item = document.createElement('li');
        item.className = 'cf-welcome__group h6 text-subdued';
        item.setAttribute('role', 'presentation');
        item.textContent = text;
        return item;
      };
      let items;
      const q = fold(query.trim());
      if (q) {
        const code = (row) => row.dataset.country.toLowerCase() === q;
        const hits = this.rows.filter((row) => code(row) || row.names.some((name) => name.includes(q)));
        const starts = (row) => (code(row) ? 0 : row.names.some((name) => name.startsWith(q)) ? 1 : 2);
        hits.sort((a, b) => starts(a) - starts(b) || byName(a, b));
        const others = this.otherCountries()
          .filter((entry) => entry.code.toLowerCase() === q || entry.names.some((name) => name.includes(q)))
          .sort((a, b) => (a.names.some((name) => name.startsWith(q)) ? 0 : 1) - (b.names.some((name) => name.startsWith(q)) ? 0 : 1))
          .map((entry) => this.otherRow(entry.code));
        if (hits.length || others.length) {
          items = [...hits, ...others];
        } else {
          const none = document.createElement('li');
          none.className = 'cf-welcome__empty text-subdued';
          none.setAttribute('role', 'presentation');
          none.textContent = list.dataset.none;
          items = [none, this.otherRow('')];
        }
      } else {
        const own = state.detected ? this.option(state.detected) : null;
        const popular = this.popular.filter((code) => code !== state.detected).map((code) => this.option(code)).filter(Boolean);
        const rest = this.rows.filter((row) => row !== own && !popular.includes(row)).sort(byName);
        items = [];
        if (own) items.push(group(list.dataset.groupLocation), own);
        if (popular.length) items.push(group(list.dataset.groupPopular), ...popular);
        items.push(group(list.dataset.groupAll), ...rest, this.otherRow(''));
      }
      this.rows.forEach((row) => {
        row.setAttribute('aria-selected', String(row.dataset.country === state.country && (state.other === null || state.other === undefined)));
        row.classList.remove('is-active');
      });
      list.replaceChildren(...items);
      this.active = -1;
    }

    search(value) {
      this.fill(value);
      this.content.querySelector('[role="listbox"]').scrollTop = 0;
      this.setActive(value.trim() ? 0 : -1);
    }

    setActive(index) {
      const options = [...this.content.querySelectorAll('[role="listbox"] .cf-welcome__option')];
      const input = this.content.querySelector('[data-country-search]');
      options.forEach((option, i) => option.classList.toggle('is-active', i === index));
      this.active = index;
      const option = options[index];
      if (option) {
        input.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      } else {
        input.removeAttribute('aria-activedescendant');
      }
    }

    searchKey(event) {
      const count = this.content.querySelectorAll('[role="listbox"] .cf-welcome__option').length;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.setActive(Math.min(this.active + 1, count - 1));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.setActive(Math.max(this.active - 1, 0));
      } else if (event.key === 'Enter') {
        event.preventDefault();
        const option = this.content.querySelectorAll('[role="listbox"] .cf-welcome__option')[this.active];
        if (option) {
          if (option.dataset.other !== undefined) this.chooseOther(option.dataset.other);
          else this.choose(option.dataset.country);
        }
      } else if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.closeList(true);
      }
    }
  }

  customElements.define('cf-welcome', CfWelcome);
})();
